import React from 'react';
import { AlertTriangle, Beaker, CheckCircle2 } from 'lucide-react';

export function DemoDataBadge({ className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 ${className}`}
      title="This section is showing demo data because the live service is unavailable or REACT_APP_DASHBOARD_USE_MOCK is enabled"
    >
      <Beaker className="w-3 h-3" />
      Demo data
    </span>
  );
}

export function SectionDataSourceBadge({ source }) {
  if (source === 'mock') {
    return <DemoDataBadge />;
  }
  if (source === 'live') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-800">
        <CheckCircle2 className="w-3 h-3" />
        Live
      </span>
    );
  }
  return null;
}

export function NoDataPanel({ message = 'No data available from RCM services.', hint, className = '' }) {
  return (
    <div className={`rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center ${className}`}>
      <p className="text-sm text-gray-600">{message}</p>
      {hint ? (
        <p className="text-xs text-amber-700 mt-2 font-medium">{hint}</p>
      ) : (
        <p className="text-xs text-gray-500 mt-1">
          Ensure the service is running and organization data exists for the selected period.
        </p>
      )}
    </div>
  );
}

export default function DashboardDataSourceNotice({ meta }) {
  if (!meta) return null;

  const { useMock, allUnavailable, allLive, liveCount, mockCount, unavailableCount, reason } = meta;

  if (reason === 'no_organization') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-900">Organization not selected</p>
            <p className="text-xs text-amber-800 mt-1">
              Select an organization to load operational metrics from RCM services.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (allLive && liveCount > 0) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-3">
        <div className="flex items-center gap-2 text-sm text-green-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>Showing live metrics from RCM services ({liveCount} data sources).</span>
        </div>
      </div>
    );
  }

  if (useMock) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <Beaker className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-900">Demo data in use</p>
            <p className="text-xs text-amber-800 mt-1">
              {mockCount} section{mockCount === 1 ? '' : 's'} showing demo values
              {liveCount > 0 ? `; ${liveCount} from live services` : ''}.
              {meta.mockEnabled
                ? ' Demo mode is enabled via REACT_APP_DASHBOARD_USE_MOCK=true.'
                : ' Some RCM services were unavailable — only failed sections use demo data.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (allUnavailable) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-900">Unable to load dashboard metrics</p>
            <p className="text-xs text-red-700 mt-1">
              No data was returned from RCM services ({unavailableCount} sources unavailable).
              Check that Denial Management, Claims, AR Posting, and related services are running.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-2 text-sm text-amber-900">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>
          Partial data: {liveCount} live, {unavailableCount} unavailable.
          Unavailable sections show &quot;No data&quot; below.
        </span>
      </div>
    </div>
  );
}
